import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getActivePlans } from '../services/pricingPlanService';
import type { PricingPlan } from '../services/pricingPlanService';

/**
 * Trial Expired Page
 *
 * Shown when a user's company trial has expired. Displays upgrade options
 * with pricing plans fetched from the API and a contact link.
 * Standalone page (no Layout wrapper since user is logged out).
 */

interface DisplayTier {
  name: string;
  description: string | null;
  price: number | null;
  priceAnnual: number | null;
  highlighted: boolean;
  badge: string | null;
  features: string[];
  notIncluded: string[];
  ctaText: string;
  ctaStyle: string;
}

function mapPlanToTier(plan: PricingPlan, isHighlighted: boolean): DisplayTier {
  let features: string[] = [];
  let notIncluded: string[] = [];
  try { features = JSON.parse(plan.features || '[]'); } catch { /* empty */ }
  try { notIncluded = JSON.parse(plan.not_included || '[]'); } catch { /* empty */ }

  return {
    name: plan.name,
    description: plan.description,
    price: plan.price_monthly,
    priceAnnual: plan.price_annual,
    highlighted: isHighlighted,
    badge: plan.highlight_badge,
    features,
    notIncluded,
    ctaText: plan.cta_text || 'Contact Us',
    ctaStyle: isHighlighted
      ? 'bg-gradient-to-r from-indigo-600 to-blue-600 text-white hover:from-indigo-700 hover:to-blue-700 shadow-lg'
      : 'border-2 border-gray-300 text-gray-700 hover:border-indigo-600 hover:text-indigo-600'
  };
}

export function TrialExpired(): React.ReactElement {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  const [tiers, setTiers] = useState<DisplayTier[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPlans = async () => {
      try {
        const plans = await getActivePlans();
        const mapped = plans.map(plan => mapPlanToTier(plan, plan.is_highlighted));
        setTiers(mapped);
      } catch (err) {
        console.error('Failed to load pricing plans:', err);
      } finally {
        setLoading(false);
      }
    };
    loadPlans();
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation Header */}
      <nav className="bg-white/80 backdrop-blur-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center">
              <img src="/logo1.png" alt="Nivoon" className="h-16" />
            </Link>
            <Link
              to="/login"
              className="text-gray-700 hover:text-indigo-600 font-medium transition-colors"
            >
              Back to Login
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-16 pb-8 bg-gradient-to-b from-slate-900 to-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* Lock Icon */}
          <div className="mx-auto w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mb-6">
            <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>

          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Your Free Trial Has Ended
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto mb-8">
            Your trial period has expired. Upgrade to a paid plan to continue using Nivoon's powerful requirements management platform.
          </p>

          {/* Billing Toggle */}
          <div className="inline-flex items-center bg-slate-700 rounded-full p-1">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                billingCycle === 'monthly'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                billingCycle === 'annual'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              Annual
              <span className="ml-2 text-xs bg-green-500 text-white px-2 py-0.5 rounded-full">
                Save 20%
              </span>
            </button>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              <span className="ml-3 text-gray-500">Loading plans...</span>
            </div>
          ) : tiers.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">Pricing information is currently unavailable.</p>
              <p className="text-gray-500 mt-2">
                Please contact us at{' '}
                <a href="mailto:info@nivoon.com" className="text-indigo-600 hover:text-indigo-500 font-medium">
                  info@nivoon.com
                </a>{' '}
                to discuss upgrade options.
              </p>
            </div>
          ) : (
            <div className={`grid grid-cols-1 md:grid-cols-2 ${tiers.length >= 4 ? 'lg:grid-cols-4' : tiers.length === 3 ? 'lg:grid-cols-3' : ''} gap-6`}>
              {tiers.map((tier) => (
                <div
                  key={tier.name}
                  className={`relative bg-white rounded-2xl shadow-md flex flex-col ${
                    tier.highlighted
                      ? 'ring-2 ring-indigo-600 shadow-xl scale-[1.02]'
                      : 'border border-gray-200'
                  }`}
                >
                  {/* Badge */}
                  {tier.badge && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white text-sm font-semibold px-4 py-1.5 rounded-full shadow-md">
                        {tier.badge}
                      </span>
                    </div>
                  )}

                  <div className="p-8 flex-1 flex flex-col">
                    {/* Tier Name */}
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{tier.name}</h3>
                    <p className="text-sm text-gray-500 mb-6">{tier.description}</p>

                    {/* Price */}
                    <div className="mb-8">
                      {tier.price !== null ? (
                        <div className="flex items-baseline">
                          <span className="text-5xl font-bold text-gray-900">
                            ${billingCycle === 'annual' ? tier.priceAnnual : tier.price}
                          </span>
                          <span className="text-gray-500 ml-2">/user/month</span>
                        </div>
                      ) : (
                        <div>
                          <span className="text-3xl font-bold text-gray-900">Custom</span>
                          <p className="text-sm text-gray-500 mt-1">Tailored to your needs</p>
                        </div>
                      )}
                      {billingCycle === 'annual' && tier.price !== null && (
                        <p className="text-sm text-gray-500 mt-1">Billed annually</p>
                      )}
                    </div>

                    {/* CTA Button — opens mailto */}
                    <a
                      href={`mailto:info@nivoon.com?subject=Upgrade to ${encodeURIComponent(tier.name)} Plan&body=Hi Nivoon team,%0A%0AI'd like to upgrade my account to the ${encodeURIComponent(tier.name)} plan.%0A%0AThank you.`}
                      className={`w-full py-3 px-6 rounded-lg font-semibold transition-all mb-8 text-center block ${tier.ctaStyle}`}
                    >
                      {tier.ctaText}
                    </a>

                    {/* Feature List */}
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-gray-900 mb-4">What's included:</p>
                      <ul className="space-y-3">
                        {tier.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start">
                            {feature.includes('Everything in') ? (
                              <>
                                <svg className="w-5 h-5 text-indigo-600 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                <span className="text-sm font-semibold text-indigo-600">{feature}</span>
                              </>
                            ) : (
                              <>
                                <svg className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                <span className="text-sm text-gray-700">{feature}</span>
                              </>
                            )}
                          </li>
                        ))}
                      </ul>

                      {/* Not Included */}
                      {tier.notIncluded.length > 0 && (
                        <div className="mt-6 pt-4 border-t border-gray-100">
                          <ul className="space-y-3">
                            {tier.notIncluded.map((feature, idx) => (
                              <li key={idx} className="flex items-start">
                                <svg className="w-5 h-5 text-gray-300 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                                <span className="text-sm text-gray-400">{feature}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-gradient-to-r from-indigo-600 to-blue-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Have questions about our plans?
          </h2>
          <p className="text-xl text-indigo-100 mb-8">
            Contact us at info@nivoon.com to discuss which plan is right for your team.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="mailto:info@nivoon.com?subject=Nivoon Plan Inquiry"
              className="px-8 py-4 bg-white text-indigo-600 font-semibold rounded-lg hover:bg-gray-100 transition-all text-lg shadow-lg"
            >
              Contact Us
            </a>
            <Link
              to="/login"
              className="px-8 py-4 bg-indigo-700 text-white font-semibold rounded-lg border-2 border-white hover:bg-indigo-800 transition-all text-lg text-center"
            >
              Back to Login
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center">
            <Link to="/" className="mb-4 md:mb-0">
              <img src="/logo1.png" alt="Nivoon" className="h-8 opacity-70" />
            </Link>
            <p className="text-sm text-gray-400">
              &copy; {new Date().getFullYear()} Nivoon. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default TrialExpired;
